import { makeAutoObservable, runInAction } from 'mobx';
import type { User } from '../types';
import { getToken, setToken, clearToken, ApiError } from '../api/utils';
import { authApi } from '../api/auth_api';

export class AuthStore {
  user: User | null = null;
  token: string | null = null;
  roles: string[] = [];
  loading = false;
  initialized = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get isAuthenticated(): boolean {
    return !!this.token && !!this.user;
  }

  get isMentor(): boolean {
    return this.roles.includes('mentor');
  }

  get hasAutoMentor(): boolean {
    return this.roles.includes('auto-mentor');
  }

  get isAssistant(): boolean {
    return this.user?.is_beta === true;
  }

  get isClient(): boolean {
    return !this.user?.is_beta || this.roles.includes('client');
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    const token = await getToken();
    if (token) {
      runInAction(() => {
        this.token = token;
        this.loading = true;
      });

      try {
        const user = await authApi.me();
        runInAction(() => {
          this.user = user;
          this.loading = false;
          this.initialized = true;
        });
        // Fetch roles after user is loaded
        await this.fetchRoles();
      } catch (err) {
        // Only clear token for actual auth failures (401), not network/server errors
        if (err instanceof ApiError && err.status === 401) {
          await clearToken();
          runInAction(() => {
            this.token = null;
            this.user = null;
            this.roles = [];
            this.loading = false;
            this.initialized = true;
          });
        } else {
          // Network/server error - keep the token, user can retry
          runInAction(() => {
            this.loading = false;
            this.initialized = true;
          });
        }
      }
    } else {
      runInAction(() => {
        this.initialized = true;
      });
    }
  }

  async fetchRoles(): Promise<void> {
    try {
      const rolesResponse = await authApi.getRoles();
      runInAction(() => {
        this.roles = rolesResponse.map((r) => r.role);
      });
    } catch {
      // Ignore errors - roles will just be empty
      runInAction(() => {
        this.roles = [];
      });
    }
  }

  async login(email: string, password: string): Promise<boolean> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const response = await authApi.login(email, password);
      await setToken(response.token);

      runInAction(() => {
        this.token = response.token;
        this.user = response.user;
        this.loading = false;
      });
      // Fetch roles after login
      await this.fetchRoles();
      return true;
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error = err instanceof Error ? err.message : 'Login failed';
      });
      return false;
    }
  }

  async register(
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ): Promise<boolean> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const response = await authApi.register(
        email,
        password,
        firstName,
        lastName
      );
      await setToken(response.token);

      runInAction(() => {
        this.token = response.token;
        this.user = response.user;
        this.loading = false;
      });
      // Fetch roles after registration
      await this.fetchRoles();
      return true;
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error = err instanceof Error ? err.message : 'Registration failed';
      });
      return false;
    }
  }

  async logout(): Promise<void> {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout API errors
    }

    await clearToken();
    runInAction(() => {
      this.token = null;
      this.user = null;
      this.roles = [];
    });
  }

  clearError(): void {
    this.error = null;
  }
}

export const authStore = new AuthStore();
